import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { FiAlertTriangle, FiChevronDown, FiChevronRight, FiDownload, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import {
	EDGE_LABEL,
	KIND_LABEL,
	buildReachabilityJson,
	buildReachabilitySvg,
	describePathSteps,
	edgeColorFor,
	nodeStyleFor,
	reachabilityFileBaseName,
	type ReachabilityReport
} from "../../Helper/reachabilityGraph";

export type ReachabilityModalProps = {
	open: boolean;
	contentId?: string | null;
	onClose: () => void;
};

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

const download = (content: string, mime: string, fileName: string) => {
	const blob = new Blob([content], { type: mime });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = fileName;
	document.body.appendChild(anchor);
	anchor.click();
	anchor.remove();
	URL.revokeObjectURL(url);
};

const VERDICT_TONE: Record<string, { bg: string; border: string; text: string; title: string }> = {
	"navigation-permanente": {
		bg: "bg-emerald-50",
		border: "border-emerald-200",
		text: "text-emerald-800",
		title: "Dans la navigation permanente"
	},
	navigation: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", title: "Accessible en naviguant" },
	"recherche-seule": { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", title: "Uniquement par la recherche" },
	"non-publie": { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", title: "Pas encore publié" }
};

/**
 * « Par où un visiteur arrive-t-il ici ? »
 *
 * Tout est calculé au moment de l'ouverture, côté serveur, à partir du menu du site et des liens
 * réellement présents dans les pages : aucun chemin n'est écrit en dur, une refonte de la
 * navigation change la réponse sans toucher au code. Le menu, les liens rapides et le pied de page
 * sont rendus sur TOUTES les pages : un itinéraire qui les emprunte ne part donc pas de l'accueil,
 * il part de n'importe où, et c'est ainsi qu'il est présenté. Le cas qui compte pour un rédacteur est le
 * verdict orange : la page est bien en ligne, mais aucune suite de clics n'y mène, seul le moteur
 * de recherche du site la trouve.
 */
const ReachabilityModal: React.FC<ReachabilityModalProps> = ({ open, contentId, onClose }) => {
	const [report, setReport] = useState<ReachabilityReport | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	// La liste des itineraires est la reponse a la question posee : depuis que les variantes qui
	// repassent par la navigation permanente sont ecartees, elle tient a l'ecran et s'affiche
	// d'entree, au lieu de forcer un clic de plus pour la deplier.
	const [detailsOpen, setDetailsOpen] = useState(true);

	useEffect(() => {
		if (!open || !contentId) return;
		let cancelled = false;
		setLoading(true);
		setError(null);
		axios
			.get<ReachabilityReport>(`${apiBase}/api/connections/reachability`, {
				params: { id: contentId },
				withCredentials: true
			})
			.then((response) => {
				if (cancelled) return;
				setReport(response.data ?? null);
			})
			.catch(() => {
				if (cancelled) return;
				setReport(null);
				setError("L'analyse n'a pas pu être calculée pour ce contenu.");
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [open, contentId, reloadKey]);

	useEffect(() => {
		if (!open) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onClose]);

	const svg = useMemo(() => (report ? buildReachabilitySvg(report) : ""), [report]);

	useEffect(() => {
		if (!report) return;
		setDetailsOpen(report.paths.length > 0);
	}, [report]);

	const exportSvg = useCallback(() => {
		if (!report) return;
		const now = new Date();
		download(svg, "image/svg+xml;charset=utf-8", `${reachabilityFileBaseName(report, now)}.svg`);
	}, [report, svg]);

	const exportJson = useCallback(() => {
		if (!report) return;
		const now = new Date();
		download(buildReachabilityJson(report, now), "application/json;charset=utf-8", `${reachabilityFileBaseName(report, now)}.json`);
	}, [report]);

	if (!open) return null;

	const tone = report ? VERDICT_TONE[report.verdict] ?? VERDICT_TONE["non-publie"] : VERDICT_TONE["non-publie"];
	const usedKinds = report ? Array.from(new Set(report.nodes.map((node) => node.kind))) : [];
	const usedEdgeKinds = report ? Array.from(new Set(report.edges.map((edge) => edge.kind))) : [];

	// Portail vers <body> : la barre laterale de l'application est en z-[1000], une modale rendue
	// dans l'arbre de la page passerait dessous. Meme convention que Modal.tsx et les tickets.
	return createPortal(
		<div className="fixed inset-0 z-[1300] flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-label="Chemin d'accès">
			<div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
				<div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
					<div className="min-w-0">
						<h2 className="text-base font-semibold text-slate-900">Chemin d'accès</h2>
						<p className="mt-0.5 truncate text-xs text-slate-500">
							{report?.title || "Chargement..."}
							{report?.publicPath ? ` · /${report.publicPath.replace(/^\/+/, "")}` : ""}
						</p>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						<button
							type="button"
							onClick={() => setReloadKey((value) => value + 1)}
							disabled={loading}
							title="Relancer l'analyse"
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
						>
							<FiRefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Recalculer
						</button>
						<button
							type="button"
							onClick={exportSvg}
							disabled={!report}
							title="Télécharger le graphe au format SVG"
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
						>
							<FiDownload className="h-3.5 w-3.5" /> Graphe (SVG)
						</button>
						<button
							type="button"
							onClick={exportJson}
							disabled={!report}
							title="Télécharger les chemins et le détail au format JSON"
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
						>
							<FiDownload className="h-3.5 w-3.5" /> Données (JSON)
						</button>
						<button type="button" onClick={onClose} aria-label="Fermer" className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
							<FiX className="h-5 w-5" />
						</button>
					</div>
				</div>

				<div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
					{loading && <p className="py-10 text-center text-sm text-slate-500">Analyse de la navigation du site...</p>}
					{!loading && error && <p className="py-10 text-center text-sm text-rose-600">{error}</p>}

					{!loading && !error && report && (
						<>
							<div className={`flex items-start gap-3 rounded-xl border ${tone.border} ${tone.bg} px-4 py-3`}>
								{report.verdict === "recherche-seule" ? (
									<FiSearch className={`mt-0.5 h-4 w-4 shrink-0 ${tone.text}`} />
								) : (
									<span
											className={`mt-1 h-2 w-2 shrink-0 rounded-full ${
												report.verdict === "navigation" || report.verdict === "navigation-permanente"
													? "bg-emerald-500"
													: "bg-slate-400"
											}`}
										/>
								)}
								<div className="min-w-0">
									<p className={`text-sm font-semibold ${tone.text}`}>{tone.title}</p>
									<p className={`mt-0.5 text-xs ${tone.text}`}>{report.verdictLabel}</p>
								</div>
							</div>

							{/* Les boucles ne sont plus signalées : elles sont impossibles par construction, un
							    écran n'apparaissant jamais deux fois dans un chemin. Un avertissement permanent
							    sur un site réel n'apprenait rien à personne. */}
							{report.truncated && (
								<div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
									<p className="flex items-start gap-2">
										<FiAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
										D'autres chemins, plus longs, existent : seuls les plus courts sont affichés.
									</p>
								</div>
							)}

							{/* Le SVG affiché EST la chaîne exportée : ce qu'on voit est ce qu'on télécharge. Il se
							    réduit pour tenir dans la modale, jamais agrandi : le graphe entier reste visible
							    sans faire défiler, et les petits arbres gardent leur taille de lecture. */}
							<div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white p-3 [&_svg]:h-auto [&_svg]:max-w-full">
								<div data-testid="reachability-graph" dangerouslySetInnerHTML={{ __html: svg }} />
							</div>

							<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
								{usedKinds.map((kind) => {
									const style = nodeStyleFor(kind);
									return (
										<span key={kind} className="inline-flex items-center gap-1.5">
											<span className="inline-block h-2.5 w-2.5 rounded-sm border" style={{ background: style.fill, borderColor: style.stroke }} />
											{KIND_LABEL[kind] ?? kind}
										</span>
									);
								})}
								{usedEdgeKinds.map((kind) => (
									<span key={`edge-${kind}`} className="inline-flex items-center gap-1.5">
										<span className="inline-block h-0.5 w-5 rounded" style={{ background: edgeColorFor(kind) }} />
										{EDGE_LABEL[kind] ?? kind}
									</span>
								))}
							</div>

							{/* Le graphe porte deja l'essentiel sur chaque noeud ; la liste reste disponible pour
							    lire un parcours phrase par phrase, repliee par defaut pour ne pas noyer le graphe. */}
							<div className="mt-5">
								{report.paths.length > 0 ? (
									<button
										type="button"
										onClick={() => setDetailsOpen((open) => !open)}
										aria-expanded={detailsOpen}
										className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
									>
										{detailsOpen ? <FiChevronDown className="h-3.5 w-3.5" /> : <FiChevronRight className="h-3.5 w-3.5" />}
										{`${report.paths.length} itinéraire${report.paths.length > 1 ? "s" : ""} vers cette page`}
									</button>
								) : (
									<h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
										Aucun itinéraire vers cette page
									</h3>
								)}
								{report.paths.length > 0 && report.edges.some((edge) => edge.isGlobal) && (
									<p className="mt-1 text-[11px] text-slate-400">
										Les itinéraires qui repassent par une page déjà présente dans la navigation permanente ne sont
										pas listés : on y accède en un clic depuis n'importe quelle page.
									</p>
								)}
								{report.paths.length === 0 ? (
									<p className="mt-2 text-sm text-slate-600">
										Aucune suite de clics ne mène ici en {report.maxDepth} étapes ou moins. Pour rendre ce contenu
										navigable, ajoutez une entrée de menu ou un lien depuis une page déjà atteignable.
									</p>
								) : !detailsOpen ? null : (
									<ol className="mt-2 space-y-3">
										{report.paths.map((path, index) => {
											const allSteps = describePathSteps(report, path);
											const clicks = path.nodeIds.length - 1;
											// Le premier lien vient du gabarit du site : il est sur toutes les pages, donc
											// l'itineraire ne commence pas a l'accueil, il commence n'importe ou. Afficher
											// « Accueil » en tete serait faux, et c'est ce qui rendait la reponse illisible.
											const fromAnywhere = Boolean(allSteps[1]?.fromAnywhere);
											const steps = fromAnywhere ? allSteps.slice(1) : allSteps;
											return (
												<li key={`path-${index}`} className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
													<p className="mb-1.5 font-semibold text-slate-500">
														Itinéraire {index + 1} · {clicks} clic{clicks > 1 ? "s" : ""}
														{fromAnywhere ? " · depuis n'importe quelle page" : ""}
													</p>
													{/* Chaque etape dit AUSSI dans quel bloc et a quelle place se trouve le lien
													    suivi : sans cela, retrouver l'entree dans une page de trente-cinq blocs
													    reste a la charge du redacteur. */}
													<ul className="space-y-1">
														{steps.map((step, position) => (
															<li key={`${index}-${position}`}>
																<div className="flex flex-wrap items-baseline gap-x-2">
																	<span className={position === steps.length - 1 ? "font-semibold text-slate-900" : "text-slate-800"}>
																		{position > 0 && <span className="mr-1 text-slate-300">{"↳"}</span>}
																		{step.label}
																	</span>
																	{step.path && <code className="text-[11px] text-slate-400">{step.path}</code>}
																</div>
																{step.via && (
																	<p className="ml-4 text-[11px]" style={{ color: edgeColorFor(step.viaKind) }}>
																		via {step.via}
																	</p>
																)}
															</li>
														))}
													</ul>
												</li>
											);
										})}
									</ol>
								)}
							</div>
						</>
					)}
				</div>
			</div>
		</div>,
		document.body
	);
};

export default ReachabilityModal;
